import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { Export24 } from 'ui/icons/aurora/24px/Export24';
import { OpenNewWindow24 } from 'ui/icons/action/OpenNewWindow24';
import { SecondaryPageHeader, HeaderTitle } from 'ui/pageHeader';
import { BUTTON_SIZES, PrimaryButton, SecondaryButton } from 'ui/button';

import { parseColsToCSVHeaders, getCSVdata } from '../utils';
import { StyledCSVLink } from './Styled';
import { SnippetUsage } from '../types';
import { useTranslations } from '../hooks/useTranslations';

type Props = {
  rows: SnippetUsage[];
  name: string;
  id: number;
  hasResults: boolean;
};
interface RouteHistory {
  prevUrl: string;
}

const PageActions = (data: Record<string, string | number>[], isActive: boolean, id: number) => {
  const { t } = useTranslations();
  return (
    <>
      <Link to={`/templates/snippet/${id}/edit`} target="_blank">
        <SecondaryButton size={BUTTON_SIZES.LARGE}>
          <OpenNewWindow24 /> {t('button.viewSnippet')}
        </SecondaryButton>
      </Link>

      {isActive ? (
        <StyledCSVLink
          filename={`snippet-${id}-reference.csv`}
          target="_blank"
          data={data}
          headers={parseColsToCSVHeaders()}
        >
          <PrimaryButton size={BUTTON_SIZES.LARGE}>
            <Export24 /> {t('button.export')}
          </PrimaryButton>
        </StyledCSVLink>
      ) : null}
    </>
  );
};

export const SnippetReferenceHeader = ({ rows, name, id, hasResults }: Props) => {
  const { t } = useTranslations();
  const history = useHistory<RouteHistory>();

  const getBreadcrumb = () => {
    // State is stored onClick from SnippetOverflowMenu component;
    const url = history.location.state ? history.location.state.prevUrl : null;

    const isDetailPage = url ? url.search('edit') >= 0 : false;

    return {
      href: isDetailPage ? `/templates/snippet/${id}/edit` : '/templates/snippets',
      content: isDetailPage ? 'SEE SNIPPET DETAIL' : 'SEE ALL SNIPPETS',
    };
  };

  const exportData = useMemo(() => {
    return getCSVdata(rows);
  }, [rows]);

  const prevUrl = useMemo(() => getBreadcrumb(), []);

  return (
    <SecondaryPageHeader
      breadcrumb={{ href: prevUrl.href, content: prevUrl.content }}
      documentTitle={`${name} | ${t('header.snippetReferences')}`}
      pageActions={PageActions(exportData, hasResults, id)}
      pageSubSection={<>{`ID: ${id}`}</>}
    >
      <HeaderTitle>{`${name} locations`}</HeaderTitle>
    </SecondaryPageHeader>
  );
};
